import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2,
  DollarSign,
  Edit,
  Save,
  Zap,
  Users,
  Crown,
  Star,
  ShoppingBag,
  Heart,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CommissionStructure = () => {
  const { isAdmin } = useAuth();
  const [structure, setStructure] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [structureRes, matrixRes] = await Promise.all([
        api.get('/commission-structure'),
        api.get('/commission-matrix'),
      ]);
      setStructure(structureRes.data);
      setMatrix(matrixRes.data);
    } catch (error) {
      toast.error('Failed to load commission structure');
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier) => {
    const icons = {
      'broker_1': Users,
      'broker_2': Star,
      'manager': Crown,
      'director': Crown,
      'retail_rep': ShoppingBag,
    };
    const Icon = icons[tier] || Users;
    return <Icon className="w-5 h-5" />;
  };

  const getTierColor = (tier) => {
    const colors = {
      'broker_1': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'broker_2': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'manager': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'director': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'retail_rep': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
    };
    return colors[tier] || colors['broker_1'];
  };

  const formatTierName = (tier) => {
    return tier.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const openEditDialog = (tier) => {
    const tierData = structure[tier] || {};
    setEditingTier(tier);
    setFormData({
      self_sold_per_kw: tierData.self_sold_per_kw || 0,
      override_broker_1: tierData.override_broker_1 || 0,
      override_broker_2: tierData.override_broker_2 || 0,
      override_manager: tierData.override_manager || 0,
      override_retail: tierData.override_retail || 0,
      override_broker_1_no_manager: tierData.override_broker_1_no_manager || 0,
      override_broker_2_no_manager: tierData.override_broker_2_no_manager || 0,
      description: tierData.description || '',
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        [editingTier]: {
          self_sold_per_kw: parseFloat(formData.self_sold_per_kw) || 0,
          description: formData.description,
        },
      };

      // Add override fields based on tier
      if (editingTier === 'manager') {
        updatePayload[editingTier].override_broker_1 = parseFloat(formData.override_broker_1) || 0;
        updatePayload[editingTier].override_broker_2 = parseFloat(formData.override_broker_2) || 0;
        updatePayload[editingTier].override_retail = parseFloat(formData.override_retail) || 0;
      } else if (editingTier === 'director') {
        updatePayload[editingTier].override_manager = parseFloat(formData.override_manager) || 0;
        updatePayload[editingTier].override_broker_1_no_manager = parseFloat(formData.override_broker_1_no_manager) || 0;
        updatePayload[editingTier].override_broker_2_no_manager = parseFloat(formData.override_broker_2_no_manager) || 0;
      }

      await api.put('/commission-structure', updatePayload);
      toast.success('Commission structure updated');
      loadData();
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update commission structure');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNonprofit = async (amount) => {
    setSaving(true);
    try {
      await api.put('/commission-structure', {
        nonprofit_donation: parseFloat(amount) || 500,
      });
      toast.success('Nonprofit donation amount updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="commission-structure-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
          Commission Structure
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Configure sales rep tiers and payout rates
        </p>
      </div>

      <Tabs defaultValue="matrix" className="space-y-6">
        <TabsList>
          <TabsTrigger value="matrix">Commission Matrix</TabsTrigger>
          <TabsTrigger value="tiers">Tier Details</TabsTrigger>
          <TabsTrigger value="rules">Commission Rules</TabsTrigger>
        </TabsList>

        {/* Matrix View */}
        <TabsContent value="matrix" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Self-Sold Commission Rates
              </CardTitle>
              <CardDescription>Base commission rates per kW when a rep sells their own deal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {matrix?.tiers?.map((tier) => (
                  <div
                    key={tier}
                    className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-center"
                  >
                    <Badge className={getTierColor(tier.toLowerCase().replace(' ', '_'))}>
                      {tier}
                    </Badge>
                    <p className="text-3xl font-bold font-mono text-slate-900 dark:text-white mt-3">
                      ${matrix?.self_sold_rates?.[tier] || 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">per kW</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Manager Overrides */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Manager Override Rates
              </CardTitle>
              <CardDescription>Additional commission managers earn on their team's deals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">On Broker 1 Deals</p>
                  <p className="text-2xl font-bold font-mono text-blue-800 dark:text-blue-200 mt-1">
                    ${matrix?.manager_overrides?.on_broker_1 || 0}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">per kW</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">On Broker 2 Deals</p>
                  <p className="text-2xl font-bold font-mono text-purple-800 dark:text-purple-200 mt-1">
                    ${matrix?.manager_overrides?.on_broker_2 || 0}
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">per kW</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">On Retail Rep (DC)</p>
                  <p className="text-2xl font-bold font-mono text-slate-800 dark:text-slate-200 mt-1">
                    ${matrix?.manager_overrides?.on_retail || 0}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">per kW</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Director Overrides */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Star className="w-5 h-5 text-emerald-500" />
                Director Override Rates
              </CardTitle>
              <CardDescription>Director earnings on team deals (conditional on team structure)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">On Manager Deals</p>
                  <p className="text-2xl font-bold font-mono text-amber-800 dark:text-amber-200 mt-1">
                    ${matrix?.director_overrides?.on_manager || 0}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">per kW</p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">On Broker 1 (No Manager)</p>
                  <p className="text-2xl font-bold font-mono text-blue-800 dark:text-blue-200 mt-1">
                    ${matrix?.director_overrides?.on_broker_1_no_manager || 0}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">per kW</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">On Broker 2 (No Manager)</p>
                  <p className="text-2xl font-bold font-mono text-purple-800 dark:text-purple-200 mt-1">
                    ${matrix?.director_overrides?.on_broker_2_no_manager || 0}
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">per kW</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Nonprofit */}
          <Card className="border-rose-200 dark:border-rose-800">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-rose-700 dark:text-rose-300">
                <Heart className="w-5 h-5" />
                SUNRAISING Donation
              </CardTitle>
              <CardDescription>
                Flat donation per eligible completed sale (deducted from rep commission)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl flex-1">
                  <p className="text-3xl font-bold font-mono text-rose-700 dark:text-rose-300">
                    ${matrix?.nonprofit_donation || 500}
                  </p>
                  <p className="text-sm text-rose-600 dark:text-rose-400">per eligible sale</p>
                  <p className="text-xs text-rose-500 mt-1">This amount is deducted from the sales rep's commission</p>
                </div>
                {isAdmin() && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      className="w-24"
                      defaultValue={matrix?.nonprofit_donation || 500}
                      id="nonprofit-amount"
                      data-testid="nonprofit-amount-input"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById('nonprofit-amount');
                        handleSaveNonprofit(input.value);
                      }}
                      disabled={saving}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="save-nonprofit-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Free DC Solar Rate */}
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Zap className="w-5 h-5" />
                FREE DC Solar Program
              </CardTitle>
              <CardDescription>Special commission rate for Free DC Solar deals in the DC market</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <p className="text-3xl font-bold font-mono text-blue-700 dark:text-blue-300">
                  $200
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">per kW (Fixed Rate)</p>
                <p className="text-xs text-blue-500 mt-2">
                  This rate applies when a lead is marked as DC Market + FREE DC Solar
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tier Details */}
        <TabsContent value="tiers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Tier Configuration</CardTitle>
              <CardDescription>Detailed settings for each sales rep tier</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Self-Sold Rate</TableHead>
                    <TableHead>Description</TableHead>
                    {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['broker_1', 'broker_2', 'manager', 'director', 'retail_rep'].map((tier) => {
                    const tierData = structure?.[tier] || {};
                    return (
                      <TableRow key={tier} data-testid={`tier-row-${tier}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTierColor(tier)}`}>
                              {getTierIcon(tier)}
                            </div>
                            <span className="font-medium">{formatTierName(tier)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono font-medium text-lg">
                            ${tierData.self_sold_per_kw || 0}/kW
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-600 dark:text-slate-400 text-sm">
                            {tierData.description || '—'}
                          </span>
                        </TableCell>
                        {isAdmin() && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(tier)}
                              data-testid={`edit-tier-${tier}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules */}
        <TabsContent value="rules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Commission Rules
              </CardTitle>
              <CardDescription>How commissions are calculated</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {matrix?.notes?.map((note, index) => (
                <div key={`note-${index}-${note.substring(0, 20)}`} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">{index + 1}</span>
                  </div>
                  <p className="text-slate-700 dark:text-slate-300">{note}</p>
                </div>
              ))}
              
              <div className="border-t pt-6 space-y-4">
                <h4 className="font-medium text-slate-900 dark:text-white">Detailed Commission Rules:</h4>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium text-blue-700 dark:text-blue-300">Broker 1</h5>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.self_sold_rates?.['Broker 1'] || 100}/kW on self-sold deals</p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium text-purple-700 dark:text-purple-300">Broker 2</h5>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.self_sold_rates?.['Broker 2'] || 200}/kW on self-sold deals</p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium text-amber-700 dark:text-amber-300">Manager</h5>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.self_sold_rates?.['Manager'] || 350}/kW if self-sold, OR:</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.manager_overrides?.on_broker_2 || 150} override on Broker 2 deals</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.manager_overrides?.on_broker_1 || 250} override on Broker 1 deals</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.manager_overrides?.on_retail || 75} on Retail sales (DC only)</p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium text-emerald-700 dark:text-emerald-300">Director</h5>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.self_sold_rates?.['Director'] || 350}/kW if self-sold, OR:</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.director_overrides?.on_manager || 50} override on Manager deals</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.director_overrides?.on_broker_1_no_manager || 250} override on Broker 1 (only if no manager for that team)</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.director_overrides?.on_broker_2_no_manager || 150} override on Broker 2 (only if no manager for that team)</p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium text-slate-700 dark:text-slate-300">Retail Rep (DC Only)</h5>
                  <p className="text-sm text-slate-600 dark:text-slate-400">• ${matrix?.self_sold_rates?.['Retail Rep'] || 50}/kW on self-sold deals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {formatTierName(editingTier || '')} Commission</DialogTitle>
            <DialogDescription>
              Update commission rates for this tier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Self-Sold Rate ($/kW)</Label>
              <Input
                type="number"
                value={formData.self_sold_per_kw}
                onChange={(e) => setFormData(prev => ({ ...prev, self_sold_per_kw: e.target.value }))}
                data-testid="edit-self-sold-rate"
              />
            </div>
            
            {editingTier === 'manager' && (
              <>
                <div className="space-y-2">
                  <Label>Override on Broker 1 ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_broker_1}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_broker_1: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Override on Broker 2 ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_broker_2}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_broker_2: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Override on Retail Rep ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_retail}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_retail: e.target.value }))}
                  />
                </div>
              </>
            )}
            
            {editingTier === 'director' && (
              <>
                <div className="space-y-2">
                  <Label>Override on Manager ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_manager}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_manager: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Override on Broker 1 (No Manager) ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_broker_1_no_manager}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_broker_1_no_manager: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Override on Broker 2 (No Manager) ($/kW)</Label>
                  <Input
                    type="number"
                    value={formData.override_broker_2_no_manager}
                    onChange={(e) => setFormData(prev => ({ ...prev, override_broker_2_no_manager: e.target.value }))}
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Tier description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-tier-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionStructure;
